import { authFetch, BASE_BACKEND_URL } from '../../auth'
import { useEffect, useMemo, useRef, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'
import { XCircle } from '@phosphor-icons/react'
import {
  getCurrentLocalDate,
  getEndDate,
  getStartDate,
  validateDateRange
} from '../../helpers/formatDate'
import { layoutMobileMediaQuery } from '../../layout/layoutConstants'
import { DicRoles } from '../../helpers/reportConstants'
import { getUserTabConfig } from './helpers/userReportTabs'
import RouteExecutionTab from './user_tab_components/RouteExecutionTab'
import ActivityBreakdownTab from './user_tab_components/ActivityBreakdownTab'
import TimestampControlTab from './user_tab_components/TimestampControlTab'
import ErrorMessages from './components/ErrorMessages'
import SharedUserReportHeader from './components/SharedUserReportHeader'
import LoadingScreen from '../../components/LoadingScreen'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { formatActivityName } from '../../helpers/utils'
import dayjs from '../../utils/dayjsConfig'
import { isSameDateRange } from '../../poc/PurchaseOrder/helpers/dateRangeUtils'
import {
  buildUnifiedVisitDetails,
  buildUnifiedVisitLookupKey
} from './helpers/buildUnifiedVisitDetails'
import TabsMui from '../../layout/TabsMui'
import AppListHeader from '../../components/title/AppListHeader'
import createReportRequestCache from './helpers/createReportRequestCache'
import { buildActivityFilterQuery } from './helpers/activityBreakdownFilters'
import ShowUserViewMobileLayout from './mobile/ShowUserViewMobileLayout'

const DEFAULT_PER_PAGE = 25
const FILTER_PER_PAGE = 25

const buildDateRangeValue = (startDate, endDate, fallbackRange) => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  if (!start.isValid() || !end.isValid()) {
    return fallbackRange
  }

  return [start, end]
}

const ShowUserView = () => {
  const [searchParams] = useSearchParams()
  const user_db_ref = searchParams.get('user_db_ref') || ''
  const reportRequest = useMemo(() => createReportRequestCache(authFetch), [user_db_ref])
  useEffect(() => () => reportRequest.dispose(), [reportRequest])
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)
  const location = useLocation()
  const navigate = useNavigate()
  const requestedTab = searchParams.get('tab')
  const activeTab = getUserTabConfig().some(({ key }) => key === requestedTab)
    ? requestedTab
    : 'route_execution'
  const [activityEffectivenessSegment, setActivityEffectivenessSegment] = useState('general')
  const [routeGeofenceSegment, setRouteGeofenceSegment] = useState('general')
  const [routeFilterSelections, setRouteFilterSelections] = useState(() => new Map())
  const [selectedPlannedDates, setSelectedPlannedDates] = useState(null)
  const [selectedExecutedDates, setSelectedExecutedDates] = useState(null)
  const [hasLoadedRoute, setHasLoadedRoute] = useState(false)
  const [hasLoadedActivity, setHasLoadedActivity] = useState(false)
  const [isPeriodUpdating, setIsPeriodUpdating] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [routeCurrentPage, setRouteCurrentPage] = useState(1)

  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)

  const [routeCountPage, setRouteCountPage] = useState(0)
  const [routeTotalCount, setRouteTotalCount] = useState(0)
  const [activityCountPage, setActivityCountPage] = useState(0)
  const [activityTotalCount, setActivityTotalCount] = useState(0)
  const [timestampCountPage, setTimestampCountPage] = useState(0)
  const [timestampTotalCount, setTimestampTotalCount] = useState(0)

  const [startDate, setStartDate] = useState(searchParams.get('start_date') || getStartDate())
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || getEndDate())
  const [hasError, setHasError] = useState(false)
  useEffect(() => {
    const nextStart = searchParams.get('start_date')
    const nextEnd = searchParams.get('end_date')
    if (nextStart && nextEnd && validateDateRange(nextStart, nextEnd).isValid) {
      setStartDate(nextStart)
      setEndDate(nextEnd)
    }
  }, [searchParams])

  const [routeExecutionData, setRouteExecutionData] = useState([])
  const [activityData, setActivityData] = useState([])
  const [activityMapData, setActivityMapData] = useState([])
  const [timestampData, setTimestampData] = useState([])
  const [timestampSummary, setTimestampSummary] = useState({})

  const [overview, setOverview] = useState(false)
  const [userRoles, setUserRoles] = useState([])
  const [isDetailsFetching, setIsDetailsFetching] = useState(activeTab !== 'route_execution')
  const [isSummaryFetching, setIsSummaryFetching] = useState(activeTab !== 'route_execution')
  const [isDownloadFetching, setIsDownloadFetching] = useState(false)
  const [isRouteInfoFetching, setIsRouteInfoFetching] = useState(true)
  const [, setIsRouteDownloadFetching] = useState(false)
  const [isRouteFilterFetching, setIsRouteFilterFetching] = useState(true)
  const [isRouteCartFetching, setIsRouteCartFetching] = useState(true)
  const isFetching =
    activeTab === 'route_execution'
      ? isRouteInfoFetching || isRouteCartFetching || isRouteFilterFetching
      : isDetailsFetching || isSummaryFetching

  useEffect(() => {
    if (activeTab === 'route_execution' && !isFetching) {
      setHasLoadedRoute(true)
      setIsPeriodUpdating(false)
    }
  }, [activeTab, isFetching])

  useEffect(() => {
    if (activeTab === 'activity_breakdown' && !isDetailsFetching && !isSummaryFetching) {
      setHasLoadedActivity(true)
    }
  }, [activeTab, isDetailsFetching, isSummaryFetching])

  const previousPeriod = useRef(`${startDate}/${endDate}`)
  useEffect(() => {
    const period = `${startDate}/${endDate}`
    if (previousPeriod.current !== period && activeTab === 'route_execution') {
      setIsPeriodUpdating(true)
    }
    if (previousPeriod.current !== period) {
      abortActivityMapRef.current?.abort()
      setActivityMapData([])
    }
    previousPeriod.current = period
  }, [startDate, endDate, activeTab])

  const [salesmanName, setSalesmanName] = useState('')
  const [invalidRangeLimit, setInvalidRangeLimit] = useState(false)
  const [invalidRangeFormat, setInvalidRangeFormat] = useState(false)
  const [invalidDateFormat, setInvalidDateFormat] = useState(false)
  const [invalidRangeOffset, setInvalidRangeOffset] = useState(false)

  const isTimestampSameDay = startDate === endDate

  const [routeCartData, setRouteCartData] = useState([])
  const [routeFilterData, setRouteFilterData] = useState({ clients: [] })
  const [routeFilterMeta, setRouteFilterMeta] = useState({
    page: 0,
    totalRecords: 0,
    loadedCount: 0,
    hasMore: true,
    loading: false,
    search: ''
  })
  const [activityFilterData, setActivityFilterData] = useState({ clients: [] })
  const [activityFilterMeta, setActivityFilterMeta] = useState({
    page: 0,
    totalRecords: 0,
    loadedCount: 0,
    hasMore: true,
    loading: false,
    search: ''
  })

  const [filterQuery, setFilterQuery] = useState('')
  const [routeSearchQuery, setRouteSearchQuery] = useState('')
  const [activitySearchQuery, setActivitySearchQuery] = useState('')
  const [plannedDateQuery, setPlannedDateQuery] = useState(null)
  const [executedDateQuery, setExecutedDateQuery] = useState(null)
  const [activityFilterSelections, setActivityFilterSelections] = useState(() => new Map())
  const [activityFilterQuery, setActivityFilterQuery] = useState('')
  const [activityDateQuery, setActivityDateQuery] = useState(null)
  const [selectedActivityDates, setSelectedActivityDates] = useState(null)
  const [selectedActivityVisitKpi, setSelectedActivityVisitKpi] = useState(null)
  const [activityKpiClientQuery, setActivityKpiClientQuery] = useState('')

  // AbortControllers separados
  const abortRouteInfoRef = useRef(null)
  const abortDetailsRef = useRef(null)
  const abortSummaryRef = useRef(null)
  const abortDownloadRef = useRef(null)
  const abortActivityMapRef = useRef(null)
  const abortFilterRef = useRef(null)
  const abortRouteCardRef = useRef(null)
  const routeFilterRequestIdRef = useRef(0)
  const lastRouteCardPeriodRef = useRef(null)
  const shouldAutoSearchDateRangeRef = useRef(false)
  const defaultDateRangeRef = useRef([dayjs(getStartDate()), dayjs(getEndDate())])
  const [dateRangeDraft, setDateRangeDraft] = useState(defaultDateRangeRef.current)

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortRouteInfoRef.current?.abort('Page unload')
      abortDetailsRef.current?.abort('Page unload')
      abortSummaryRef.current?.abort('Page unload')
      abortDownloadRef.current?.abort('Page unload')
      abortActivityMapRef.current?.abort('Page unload')
      abortFilterRef.current?.abort('Page unload')
      abortRouteCardRef.current?.abort('Page unload')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      abortRouteInfoRef.current?.abort()
      abortDetailsRef.current?.abort()
      abortSummaryRef.current?.abort()
      abortDownloadRef.current?.abort()
      abortActivityMapRef.current?.abort()
      abortFilterRef.current?.abort()
      abortRouteCardRef.current?.abort()
    }
  }, [])

  useEffect(
    () => () => {
      abortRouteInfoRef.current?.abort()
      abortRouteCardRef.current?.abort()
      abortDetailsRef.current?.abort()
      abortSummaryRef.current?.abort()
      abortActivityMapRef.current?.abort()
    },
    [activeTab, startDate, endDate, user_db_ref]
  )

  useEffect(() => {
    if (!validateDates()) return
    if (activeTab !== 'route_execution') return

    let cancelled = false
    void (async () => {
      await fetchRouteInfoFromServer()
      if (cancelled) return
      const periodKey = `${user_db_ref}|${startDate}|${endDate}`
      if (lastRouteCardPeriodRef.current === periodKey) {
        setIsRouteCartFetching(false)
        return
      }
      const loaded = await fetchRouteCartInfoFromServer()
      if (!cancelled && loaded) lastRouteCardPeriodRef.current = periodKey
    })()

    return () => {
      cancelled = true
    }
  }, [
    activeTab,
    routeCurrentPage,
    routeSearchQuery,
    filterQuery,
    plannedDateQuery,
    executedDateQuery,
    startDate,
    endDate,
    perPage,
    user_db_ref
  ])

  useEffect(() => {
    if (!validateDates()) return
    if (activeTab == 'activity_breakdown') {
      fetchDetailsFromServer('activity_breakdown')
    }
  }, [
    activeTab,
    currentPage,
    startDate,
    endDate,
    perPage,
    activityFilterQuery,
    activityDateQuery,
    activitySearchQuery,
    activityKpiClientQuery
  ])

  useEffect(() => {
    if (!validateDates()) return
    if (activeTab == 'timestamp_control') {
      fetchDetailsFromServer('timestamp_control')
    }
  }, [activeTab, currentPage, startDate, endDate, perPage])

  const previousActivityFilterSelections = useRef(activityFilterSelections)
  useEffect(() => {
    if (previousActivityFilterSelections.current === activityFilterSelections) return
    previousActivityFilterSelections.current = activityFilterSelections
    setActivityFilterQuery(buildActivityFilterQuery(activityFilterSelections))
    setCurrentPage(1)
  }, [activityFilterSelections])

  useEffect(() => {
    if (!validateDates() || activeTab !== 'route_execution') return
    fetchRouteFilterInfoFromServer({ page: 1 })
  }, [activeTab, startDate, endDate, user_db_ref])

  useEffect(() => {
    if (!validateDates() || activeTab !== 'activity_breakdown') return
    fetchRouteFilterInfoFromServer({ page: 1 })
  }, [activeTab, startDate, endDate, user_db_ref])

  useEffect(() => {
    if (!validateDates() || activeTab != 'activity_breakdown') return
    fetchActivitySummaryFromServer()
  }, [activeTab, startDate, endDate])

  useEffect(() => {
    if (!validateDates() || activeTab != 'timestamp_control') return
    fetchTimestampSummaryFromServer()
  }, [activeTab, startDate, endDate])

  useEffect(() => {
    if (!shouldAutoSearchDateRangeRef.current) {
      return
    }

    shouldAutoSearchDateRangeRef.current = false
    searchFromDates()
  }, [endDate, startDate])

  const dateRangeValue = useMemo(
    () => buildDateRangeValue(startDate, endDate, defaultDateRangeRef.current),
    [endDate, startDate]
  )

  useEffect(() => {
    setDateRangeDraft(dateRangeValue)
  }, [dateRangeValue])

  const isDefaultDateRange = useMemo(
    () => isSameDateRange(dateRangeValue, defaultDateRangeRef.current),
    [dateRangeValue]
  )

  const routeVisitDetailsLookup = useMemo(() => {
    const activityDetails = routeExecutionData.flatMap((row) => row.activity_details || [])
    const timestampDetails = routeExecutionData.flatMap((row) => row.timestamp_details || [])

    return buildUnifiedVisitDetails({
      activityDownloadData: activityDetails,
      timestampDownloadData: timestampDetails,
      formatActivityName
    })
  }, [routeExecutionData, formatActivityName])

  const applyValidationResult = (result) => {
    setInvalidRangeOffset(result.invalidRangeOffset)
    setInvalidRangeLimit(result.invalidRangeLimit)
    setInvalidRangeFormat(result.invalidRangeFormat)
    setInvalidDateFormat(result.invalidDateFormat)
  }

  const persistPeriod = (rangeStart, rangeEnd) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('start_date', rangeStart)
    nextParams.set('end_date', rangeEnd)
    navigate({ search: nextParams.toString() }, { replace: true })
  }

  const searchFromDates = (draftStart, draftEnd) => {
    const rangeStart = draftStart !== undefined ? draftStart : startDate
    const rangeEnd = draftEnd !== undefined ? draftEnd : endDate
    const result = validateDateRange(rangeStart, rangeEnd)
    applyValidationResult(result)
    if (!result.isValid) return

    if (activeTab === 'route_execution' && (rangeStart !== startDate || rangeEnd !== endDate)) {
      setIsPeriodUpdating(true)
    }
    if (draftStart !== undefined && draftEnd !== undefined) {
      setStartDate(draftStart)
      setEndDate(draftEnd)
      persistPeriod(draftStart, draftEnd)
    }

    setCurrentPage(1)
    setRouteCurrentPage(1)
  }

  const handleDateRangeChange = (dates) => {
    if (!Array.isArray(dates) || dates.length === 0) {
      setDateRangeDraft(dateRangeValue)
      return
    }

    const sortedDates = [...dates].sort((a, b) => a.valueOf() - b.valueOf())
    setDateRangeDraft(sortedDates)

    if (sortedDates.length < 2) return

    const nextStart = sortedDates[0]
    const nextEnd = sortedDates[sortedDates.length - 1]
    const validation = validateDateRange(
      nextStart.format('YYYY-MM-DD'),
      nextEnd.format('YYYY-MM-DD')
    )
    applyValidationResult(validation)
    if (!validation.isValid) return

    if (
      activeTab === 'route_execution' &&
      (nextStart.format('YYYY-MM-DD') !== startDate || nextEnd.format('YYYY-MM-DD') !== endDate)
    ) {
      setIsPeriodUpdating(true)
    }
    shouldAutoSearchDateRangeRef.current = true
    setCurrentPage(1)
    setRouteCurrentPage(1)
    setStartDate(nextStart.format('YYYY-MM-DD'))
    setEndDate(nextEnd.format('YYYY-MM-DD'))
    persistPeriod(nextStart.format('YYYY-MM-DD'), nextEnd.format('YYYY-MM-DD'))
  }

  const handleActiveTabChange = (nextTab) => {
    if (nextTab === activeTab) return

    // Los filtros pertenecen a la pestaña que se está dejando. Al cambiar de
    // contexto, ambas consultas vuelven a su estado base para no trasladar una
    // búsqueda, rango o KPI aplicado a la siguiente pestaña.
    setRouteFilterSelections(new Map())
    setFilterQuery('')
    setRouteSearchQuery('')
    setPlannedDateQuery(null)
    setExecutedDateQuery(null)
    setSelectedPlannedDates(null)
    setSelectedExecutedDates(null)
    setRouteGeofenceSegment('general')

    setActivityFilterSelections(new Map())
    setActivityFilterQuery('')
    setActivitySearchQuery('')
    setActivityDateQuery(null)
    setSelectedActivityDates(null)
    setSelectedActivityVisitKpi(null)
    setActivityKpiClientQuery('')
    setActivityEffectivenessSegment('general')

    if (nextTab === 'route_execution') {
      setHasLoadedRoute(false)
      setIsRouteInfoFetching(true)
      setIsRouteCartFetching(true)
    } else {
      setIsDetailsFetching(true)
    }
    setCurrentPage(1)
    setRouteCurrentPage(1)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', nextTab)
    nextParams.set('start_date', startDate)
    nextParams.set('end_date', endDate)
    navigate({ search: nextParams.toString() })
  }

  const handleBackToReports = () => {
    navigate('/reportes', { state: { reportIndexState: location.state?.reportIndexState } })
  }

  const handleDateRangeReset = () => {
    const [defaultStart, defaultEnd] = defaultDateRangeRef.current

    if (activeTab === 'route_execution' && !isDefaultDateRange) setIsPeriodUpdating(true)
    setDateRangeDraft(defaultDateRangeRef.current)
    shouldAutoSearchDateRangeRef.current = true
    setCurrentPage(1)
    setRouteCurrentPage(1)
    setStartDate(defaultStart.format('YYYY-MM-DD'))
    setEndDate(defaultEnd.format('YYYY-MM-DD'))
    persistPeriod(defaultStart.format('YYYY-MM-DD'), defaultEnd.format('YYYY-MM-DD'))
  }

  const handlePaginationClick = (event) => {
    // Esta funcion se cambio para manejar la paginacion de los 3 tabs de detalles de usuario.
    const target = event.target.tagName === 'A' ? event.target : event.target.closest('a')
    if (!target) return
    const currentTabPage = activeTab == 'route_execution' ? routeCurrentPage : currentPage
    const totalPages =
      activeTab == 'route_execution'
        ? routeCountPage
        : activeTab == 'activity_breakdown'
          ? activityCountPage
          : timestampCountPage
    let newPage = currentTabPage
    switch (target.id) {
      case 'first-page':
        newPage = 1
        break
      case 'last-page':
        newPage = totalPages
        break
      case 'previous-page':
        if (currentTabPage > 1) newPage = currentTabPage - 1
        break
      case 'next-page':
        if (currentTabPage < totalPages) newPage = currentTabPage + 1
        break
      default:
        return
    }

    if (newPage !== currentTabPage) {
      if (activeTab == 'route_execution') {
        setRouteCurrentPage(newPage)
      } else {
        setCurrentPage(newPage)
      }
    }
  }

  const handlePaginationPageChange = (newPage) => {
    const currentTabPage = activeTab == 'route_execution' ? routeCurrentPage : currentPage

    if (!newPage || newPage === currentTabPage) return

    if (activeTab == 'route_execution') {
      setRouteCurrentPage(newPage)
    } else {
      setCurrentPage(newPage)
    }
  }

  const validateDates = () => {
    const result = validateDateRange(startDate, endDate)
    applyValidationResult(result)
    return result.isValid
  }

  const buildActivityBreakdownQueryString = () => {
    const hasFilters =
      activityFilterQuery || activityDateQuery || activitySearchQuery || activityKpiClientQuery
    return `${hasFilters ? '&filter_search=true' : ''}${activityFilterQuery || ''}${activityDateQuery || ''}${activityKpiClientQuery || ''}${activitySearchQuery ? `&search=${encodeURIComponent(activitySearchQuery)}` : ''}`
  }

  const fetchDetailsFromServer = async (
    tab = activeTab,
    page = currentPage,
    rangeStart = startDate,
    rangeEnd = endDate
  ) => {
    if (abortDetailsRef.current) {
      abortDetailsRef.current.abort()
    }

    const controller = new AbortController()
    abortDetailsRef.current = controller

    setIsDetailsFetching(true)
    setHasError(false)

    const activityQuery = tab === 'activity_breakdown' ? buildActivityBreakdownQueryString() : ''

    await reportRequest(
      `${BASE_BACKEND_URL}/reports/ubiqua_app/user?user_db_ref=${user_db_ref}&start_date=${rangeStart}&end_date=${rangeEnd}&page=${page}&per_page=${perPage}&tab=${tab}${activityQuery}`,
      {
        method: 'GET',
        signal: controller.signal
      }
    )
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data || controller.signal.aborted) return
        if (data.errors?.length) throw new Error(data.errors.join(', '))
        const results = data.results || []
        const totalRecords = Number(data.total_records) || 0
        const totalPages = Math.ceil(totalRecords / perPage)
        const validPage = Math.min(page, Math.max(1, totalPages))
        if (validPage !== page) {
          setCurrentPage(validPage)
          return
        }

        if (tab === 'activity_breakdown') {
          setActivityData(results)
          setActivityTotalCount(totalRecords)
          setActivityCountPage(totalPages)
          setSalesmanName((currentName) => currentName || results[0]?.salesman_name || '')
        } else {
          setTimestampData(results)
          setTimestampCountPage(totalPages)
          setTimestampTotalCount(totalRecords)
          setSalesmanName((currentName) => currentName || results[0]?.salesman_name || '')
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          console.error('Error fetchDetailsFromServer:', error)
          setHasError(true)
          setActivityData([])
          setTimestampData([])
          setActivityCountPage(0)
          setActivityTotalCount(0)
          setTimestampCountPage(0)
          setTimestampTotalCount(0)
        }
      })
    if (!controller.signal.aborted) setIsDetailsFetching(false)
  }

  const fetchDownloadData = async (tab = activeTab, options = {}) => {
    // Managed downloads own their cancellation and can outlive this route.
    if (!options.signal) abortDownloadRef.current?.abort()
    const controller = options.signal ? { signal: options.signal } : new AbortController()
    if (!options.signal) abortDownloadRef.current = controller
    setIsRouteDownloadFetching(true)
    setIsDownloadFetching(true)
    setHasError(false)

    try {
      const url =
        tab === 'route_execution'
          ? `${BASE_BACKEND_URL}/reports/route_execution_user?user_uid=${user_db_ref}&download_info_only=true&include_business=true&start_date=${startDate}&end_date=${endDate}${filterQuery || plannedDateQuery || executedDateQuery || routeSearchQuery ? '&filter_search=true' : ''}${filterQuery || ''}${plannedDateQuery || ''}${executedDateQuery || ''}${routeSearchQuery ? `&search=${encodeURIComponent(routeSearchQuery)}` : ''}`
          : `${BASE_BACKEND_URL}/reports/ubiqua_app/user?user_db_ref=${user_db_ref}&start_date=${startDate}&end_date=${endDate}&tab=${tab}&download=true&include_business=true${tab === 'activity_breakdown' ? buildActivityBreakdownQueryString() : ''}`
      const response = await authFetch(url, { method: 'GET', signal: controller.signal })
      if (!response || !response.ok) throw new Error('No se pudieron cargar los datos de descarga')

      const data = await response.json()
      if (data?.errors?.length) throw new Error(data.errors.join(', '))

      const results = data?.results || []
      if (tab !== 'route_execution') return { results, business: data.business }

      return {
        results,
        business: data.business,
        activityDetails: results.flatMap((row) => row.activity_details || []),
        timestampDetails: results.flatMap((row) => row.timestamp_details || [])
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetchDownloadData:', error)
        setHasError(true)
      }
      throw error
    } finally {
      setIsRouteDownloadFetching(false)
      setIsDownloadFetching(false)
    }
  }

  const fetchActivityMapData = async () => {
    abortActivityMapRef.current?.abort()
    const controller = new AbortController()
    abortActivityMapRef.current = controller

    try {
      const response = await reportRequest(
        `${BASE_BACKEND_URL}/reports/ubiqua_app/user?user_db_ref=${user_db_ref}&start_date=${startDate}&end_date=${endDate}&tab=activity_breakdown&download=true`,
        { method: 'GET', signal: controller.signal }
      )
      if (!response || controller.signal.aborted) return

      const data = await response.json()
      if (data?.errors?.length) throw new Error(data.errors.join(', '))
      setActivityMapData(data?.results || [])
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetchActivityMapData:', error)
        setHasError(true)
      }
      throw error
    }
  }

  const fetchRouteInfoFromServer = async (
    page = routeCurrentPage,
    rangeStart = startDate,
    rangeEnd = endDate
  ) => {
    abortRouteInfoRef.current?.abort()
    const controller = new AbortController()
    abortRouteInfoRef.current = controller
    setIsRouteInfoFetching(true)
    setHasError(false)

    const routeUrl = `${BASE_BACKEND_URL}/reports/route_execution_user?user_uid=${user_db_ref}&start_date=${rangeStart}&end_date=${rangeEnd}&page=${page}&per_page=${perPage}${filterQuery || plannedDateQuery || executedDateQuery || routeSearchQuery ? '&filter_search=true' : ''}${filterQuery || ''}${plannedDateQuery || ''}${executedDateQuery || ''}${routeSearchQuery ? `&search=${encodeURIComponent(routeSearchQuery)}` : ''}`

    await reportRequest(routeUrl, { method: 'GET', signal: controller.signal })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data || controller.signal.aborted) return
        if (data.errors?.length) throw new Error(data.errors.join(', '))
        const results = data.results || []
        const totalRecords = Number(data.total_records) || 0
        const totalPages = Math.ceil(totalRecords / perPage)
        const validPage = Math.min(page, Math.max(1, totalPages))
        if (validPage !== page) {
          setRouteCurrentPage(validPage)
          return
        }
        setRouteExecutionData(results)
        setRouteTotalCount(totalRecords)
        setRouteCountPage(totalPages)
        setSalesmanName((currentName) => currentName || results[0]?.user_name || '')
      })
      .catch((error) => {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          console.error('Error fetchRouteInfoFromServer:', error)
          setHasError(true)
          setRouteExecutionData([])
          setRouteCountPage(0)
          setRouteTotalCount(0)
        }
      })
    if (!controller.signal.aborted) setIsRouteInfoFetching(false)
  }

  const fetchRouteRowDetailsFromServer = async (row) => {
    const lookupKey = buildUnifiedVisitLookupKey(row)

    if (!lookupKey) {
      return []
    }

    return routeVisitDetailsLookup.detailsByVisitKey?.[lookupKey] || []
  }

  const fetchRouteCartInfoFromServer = async (rangeStart = startDate, rangeEnd = endDate) => {
    abortRouteCardRef.current?.abort()
    const controller = new AbortController()
    abortRouteCardRef.current = controller
    setIsRouteCartFetching(true)

    try {
      const response = await reportRequest(
        `${BASE_BACKEND_URL}/reports/route_execution_user?user_uid=${user_db_ref}&card_info_only=true&start_date=${rangeStart}&end_date=${rangeEnd}`,
        { method: 'GET', signal: controller.signal }
      )
      if (!response || controller.signal.aborted) return false
      const data = await response.json()
      if (controller.signal.aborted) return false
      if (data?.errors?.length) throw new Error(data.errors.join(', '))
      setRouteCartData(data || {})
      return true
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Error fetchRouteCartInfoFromServer:', error)
        setHasError(true)
        setRouteCartData({})
      }
      return false
    } finally {
      if (!controller.signal.aborted) setIsRouteCartFetching(false)
    }
  }

  const fetchActivitySummaryFromServer = async (rangeStart = startDate, rangeEnd = endDate) => {
    abortSummaryRef.current?.abort()
    const controller = new AbortController()
    abortSummaryRef.current = controller
    setIsSummaryFetching(true)
    setHasError(false)

    try {
      const baseUrl = `${BASE_BACKEND_URL}/reports/ubiqua_app/user?user_db_ref=${user_db_ref}&start_date=${rangeStart}&end_date=${rangeEnd}`
      const summaryResponse = await reportRequest(`${baseUrl}&tab=activity_summary`, {
        method: 'GET',
        signal: controller.signal
      })
      if (!summaryResponse) return

      const data = await summaryResponse.json()
      if (controller.signal.aborted) return
      if (data?.errors?.length) throw new Error(data.errors.join(', '))
      const summary = data?.results?.[0] || {}
      setOverview(summary)
      setSalesmanName((currentName) => summary.user_name || currentName)
      const roles = parseUserRoles(summary.user_active_roles || '')
        .split(', ')
        .filter(Boolean)
      setUserRoles(roles)
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Error fetchActivitySummaryFromServer:', error)
        setHasError(true)
      }
    } finally {
      if (!controller.signal.aborted) setIsSummaryFetching(false)
    }
  }

  const fetchTimestampSummaryFromServer = async (rangeStart = startDate, rangeEnd = endDate) => {
    abortSummaryRef.current?.abort()
    const controller = new AbortController()
    abortSummaryRef.current = controller
    setIsSummaryFetching(true)
    try {
      const response = await reportRequest(
        `${BASE_BACKEND_URL}/reports/ubiqua_app/user?user_db_ref=${user_db_ref}&start_date=${rangeStart}&end_date=${rangeEnd}&tab=timestamp_summary`,
        { method: 'GET', signal: controller.signal }
      )
      if (!response) return

      const data = await response.json()
      if (controller.signal.aborted) return
      if (data?.errors?.length) throw new Error(data.errors.join(', '))
      setTimestampSummary(data?.results?.[0] || {})
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Error fetchTimestampSummaryFromServer:', error)
        setHasError(true)
      }
    } finally {
      if (!controller.signal.aborted) setIsSummaryFetching(false)
    }
  }

  const fetchRouteFilterInfoFromServer = async ({ page = 1, search = '', append = false } = {}) => {
    const isActivityTab = activeTab === 'activity_breakdown'
    const setFilterData = isActivityTab ? setActivityFilterData : setRouteFilterData
    const setFilterMeta = isActivityTab ? setActivityFilterMeta : setRouteFilterMeta
    abortFilterRef.current?.abort()
    const controller = new AbortController()
    abortFilterRef.current = controller
    const requestId = routeFilterRequestIdRef.current + 1
    routeFilterRequestIdRef.current = requestId

    if (!append && !isActivityTab) setIsRouteFilterFetching(true)
    setFilterMeta((current) => ({ ...current, loading: true, search }))
    setHasError(false)

    const filterUrl =
      activeTab === 'activity_breakdown'
        ? `${BASE_BACKEND_URL}/reports/ubiqua_app/user?user_db_ref=${user_db_ref}&start_date=${startDate}&end_date=${endDate}&tab=activity_filters&filter_page=${page}&filter_per_page=${FILTER_PER_PAGE}${search ? `&filter_search_query=${encodeURIComponent(search)}` : ''}`
        : `${BASE_BACKEND_URL}/reports/route_execution_user?user_uid=${user_db_ref}&start_date=${startDate}&end_date=${endDate}&filter_info_only=true&filter_page=${page}&filter_per_page=${FILTER_PER_PAGE}${search ? `&filter_search_query=${encodeURIComponent(search)}` : ''}`

    await reportRequest(filterUrl, { method: 'GET', signal: controller.signal })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        if (!data || controller.signal.aborted || routeFilterRequestIdRef.current !== requestId)
          return
        if (data.errors?.length) throw new Error(data.errors.join(', '))

        const nextClients = data.results || []
        setFilterData((current) => {
          const previousClients = append ? current.clients || [] : []
          const clientsByUid = new Map(previousClients.map((client) => [client.uid, client]))
          nextClients.forEach((client) => clientsByUid.set(client.uid, client))
          return { clients: Array.from(clientsByUid.values()), activities: data.activities || [] }
        })
        setFilterMeta((current) => {
          const loadedCount = append ? current.loadedCount + nextClients.length : nextClients.length
          const totalRecords = Number(data.total_records) || 0
          return {
            ...current,
            page,
            totalRecords,
            loadedCount,
            hasMore: loadedCount < totalRecords,
            loading: false,
            search
          }
        })
      })
      .catch((error) => {
        if (error.name === 'AbortError' || routeFilterRequestIdRef.current !== requestId) return
        console.error('Error fetchRouteFilterInfoFromServer:', error)
        setHasError(true)
        setFilterMeta((current) => ({ ...current, loading: false }))
      })
    if (!append && !isActivityTab && routeFilterRequestIdRef.current === requestId) {
      setIsRouteFilterFetching(false)
    }
  }

  const handleRouteFilterSearch = (search) => {
    fetchRouteFilterInfoFromServer({ page: 1, search, append: false })
  }

  const handleRouteFilterLoadMore = () => {
    const filterMeta = activeTab === 'activity_breakdown' ? activityFilterMeta : routeFilterMeta
    if (filterMeta.loading || !filterMeta.hasMore) return
    fetchRouteFilterInfoFromServer({
      page: filterMeta.page + 1,
      search: filterMeta.search,
      append: true
    })
  }

  const parseUserRoles = (userActiveRoles) => {
    const rolesSet = new Set()
    const rolesRaw = userActiveRoles.split('~')
    for (const roleRaw of rolesRaw) {
      const roles = roleRaw?.replace(/{|}/g, '')?.split(',')
      for (const role of roles) {
        if (role) rolesSet.add(DicRoles[role])
      }
    }
    return Array.from(rolesSet).join(', ')
  }

  const sharedHeaderDownloadProps = useMemo(
    () => ({
      activeTab,
      startDate,
      endDate,
      salesmanName,
      formatActivityName,
      onLoadDownloadData: fetchDownloadData,
      isDownloadFetching,
      isDataUpdating:
        activeTab === 'route_execution' ? isPeriodUpdating || !hasLoadedRoute : isFetching,
      isTimestampSameDay,
      timestampSummary,
      routeCartData,
      geofenceSegment: routeGeofenceSegment,
      activityOverview: overview,
      activityEffectivenessSegment,
      salesmanCode: user_db_ref
    }),
    [
      activeTab,
      endDate,
      formatActivityName,
      routeCartData,
      routeGeofenceSegment,
      overview,
      activityEffectivenessSegment,
      user_db_ref,
      salesmanName,
      startDate,
      isDownloadFetching,
      isPeriodUpdating,
      hasLoadedRoute,
      isFetching,
      filterQuery,
      plannedDateQuery,
      executedDateQuery,
      routeSearchQuery,
      activitySearchQuery,
      activityFilterQuery,
      activityDateQuery,
      activityKpiClientQuery,
      isTimestampSameDay,
      timestampSummary
    ]
  )

  const totalPaginationPages =
    activeTab === 'route_execution'
      ? routeCountPage
      : activeTab === 'activity_breakdown'
        ? activityCountPage
        : timestampCountPage

  const currentPaginationPage = activeTab === 'route_execution' ? routeCurrentPage : currentPage
  const isRouteExecutionTab = activeTab === 'route_execution'

  const tabBodies = (
    <>
      {activeTab == 'route_execution' && (
        <RouteExecutionTab
          startDate={startDate}
          endDate={endDate}
          data={routeExecutionData}
          currentPage={routeCurrentPage}
          setCurrentPage={setRouteCurrentPage}
          totalPages={routeCountPage}
          handlePaginationClick={handlePaginationClick}
          salesmanName={salesmanName}
          searchQuery={routeSearchQuery}
          onSearchQuery={(query) => {
            setRouteSearchQuery(query)
            setRouteCurrentPage(1)
          }}
          routeFilterData={routeFilterData}
          routeFilterLoading={routeFilterMeta.loading}
          routeFilterHasMore={routeFilterMeta.hasMore}
          routeFilterSearchQuery={routeFilterMeta.search}
          onRouteFilterSearch={handleRouteFilterSearch}
          onRouteFilterLoadMore={handleRouteFilterLoadMore}
          routeCartData={routeCartData}
          geofenceSegment={routeGeofenceSegment}
          onGeofenceSegmentChange={setRouteGeofenceSegment}
          plannedDateQuery={plannedDateQuery}
          setPlannedDateQuery={setPlannedDateQuery}
          executedDateQuery={executedDateQuery}
          setExecutedDateQuery={setExecutedDateQuery}
          filterQuery={filterQuery}
          setFilterQuery={setFilterQuery}
          isRouteInfoFetching={isRouteInfoFetching}
          isKpiUpdating={isPeriodUpdating}
          filterSelections={routeFilterSelections}
          onFilterSelectionsChange={setRouteFilterSelections}
          selectedPlannedDates={selectedPlannedDates}
          onSelectedPlannedDatesChange={setSelectedPlannedDates}
          selectedExecutedDates={selectedExecutedDates}
          onSelectedExecutedDatesChange={setSelectedExecutedDates}
          onFetchRowDetails={fetchRouteRowDetailsFromServer}
          formatActivityName={formatActivityName}
          routeVisitDetailsByKey={routeVisitDetailsLookup.detailsByVisitKey}
          routeVisitSummaryByKey={routeVisitDetailsLookup.visitSummaryByKey}
          isMobileLayout={isMobileLayout}
          totalCount={routeTotalCount}
          perPage={perPage}
          onRowsPerPageChange={(nextPerPage) => {
            setPerPage(nextPerPage)
            setRouteCurrentPage(1)
          }}
        />
      )}
      {activeTab == 'activity_breakdown' && (
        <ActivityBreakdownTab
          data={activityData}
          perPage={perPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={activityCountPage}
          totalCount={activityTotalCount}
          handlePaginationClick={handlePaginationClick}
          salesmanName={salesmanName}
          startDate={startDate}
          endDate={endDate}
          overview={overview}
          effectivenessSegment={activityEffectivenessSegment}
          onEffectivenessSegmentChange={setActivityEffectivenessSegment}
          userRoles={userRoles}
          activityMapData={activityMapData}
          onLoadActivityMapData={fetchActivityMapData}
          formatActivityName={formatActivityName}
          isMobileLayout={isMobileLayout}
          searchQuery={activitySearchQuery}
          onSearchQuery={(query) => {
            setActivitySearchQuery(query)
            setCurrentPage(1)
          }}
          routeFilterData={activityFilterData}
          routeFilterLoading={activityFilterMeta.loading}
          routeFilterHasMore={activityFilterMeta.hasMore}
          routeFilterSearchQuery={activityFilterMeta.search}
          onRouteFilterSearch={handleRouteFilterSearch}
          onRouteFilterLoadMore={handleRouteFilterLoadMore}
          isActivityInfoFetching={isDetailsFetching}
          isKpiUpdating={isSummaryFetching}
          filterSelections={activityFilterSelections}
          onFilterSelectionsChange={setActivityFilterSelections}
          activityDateQuery={activityDateQuery}
          setActivityDateQuery={setActivityDateQuery}
          selectedActivityDates={selectedActivityDates}
          onSelectedActivityDatesChange={setSelectedActivityDates}
          selectedVisitKpi={selectedActivityVisitKpi}
          onSelectedVisitKpiChange={setSelectedActivityVisitKpi}
          onActivityKpiClientQueryChange={setActivityKpiClientQuery}
          onRowsPerPageChange={(nextPerPage) => {
            setPerPage(nextPerPage)
            setCurrentPage(1)
          }}
        />
      )}
      {activeTab == 'timestamp_control' && (
        <TimestampControlTab
          currentPage={currentPage}
          totalPages={timestampCountPage}
          totalCount={timestampTotalCount}
          perPage={perPage}
          setCurrentPage={setCurrentPage}
          onRowsPerPageChange={(nextPerPage) => {
            setPerPage(nextPerPage)
            setCurrentPage(1)
          }}
          salesmanName={salesmanName}
          timestampData={timestampData}
          timestampSummary={timestampSummary}
          isTimestampSameDay={isTimestampSameDay}
          isMobileLayout={isMobileLayout}
        />
      )}
    </>
  )

  const isReportLoading =
    validateDateRange(startDate, endDate).isValid &&
    (isRouteExecutionTab
      ? !hasLoadedRoute
      : activeTab === 'activity_breakdown'
        ? !hasLoadedActivity
        : isFetching)

  const reportContent = (
    <div aria-busy={isReportLoading}>
      {isReportLoading ? <LoadingScreen text="Cargando..." minHeight="320px" /> : tabBodies}
    </div>
  )

  return (
    <>
      <ErrorMessages
        invalidRangeOffset={invalidRangeOffset}
        invalidRangeLimit={invalidRangeLimit}
        invalidRangeFormat={invalidRangeFormat}
        invalidDateFormat={invalidDateFormat}
      />
      <UbiquaTowerToastAlert
        open={hasError}
        id={`user-report-load-error-${activeTab}`}
        title="No se pudo cargar el reporte"
        message="Verifica tu conexión e intenta nuevamente."
        icon={<XCircle size={18} weight="regular" color="#D33B3B" aria-hidden="true" />}
        colors={{ iconBackground: '#FDECEC', progress: '#D33B3B' }}
        onClose={() => setHasError(false)}
      />
      <div className={`app-list-shell ubiqua-type${isMobileLayout ? '' : ' report-desktop'}`}>
        <AppListHeader
          title={`Reporte: ${[user_db_ref, salesmanName].filter(Boolean).join(' - ') || 'Usuario'}`}
          subtitle="Detalles de actividades del usuario"
          backLabel="Volver a reportes"
          onBack={handleBackToReports}
        />
        <div className="index-table app-list-content">
          <div
            className={`app-report-content route-report-screen${
              isRouteExecutionTab ? ' route-execution-user-screen' : ''
            }`}
          >
            {isMobileLayout ? (
              <ShowUserViewMobileLayout
                activeTab={activeTab}
                setActiveTab={handleActiveTabChange}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                today={getCurrentLocalDate()}
                searchFromDates={searchFromDates}
                hasError={false}
                isFetching={false}
                downloadProps={sharedHeaderDownloadProps}
                currentPaginationPage={currentPaginationPage}
                totalPaginationPages={totalPaginationPages}
                showPagination={
                  isReportLoading
                    ? false
                    : isRouteExecutionTab
                      ? routeTotalCount > 10
                      : activeTab === 'activity_breakdown'
                        ? activityTotalCount > 10
                        : undefined
                }
                paginationDisabled={
                  (isRouteExecutionTab && isRouteInfoFetching) ||
                  (activeTab === 'activity_breakdown' && isDetailsFetching)
                }
                onPaginationPageChange={handlePaginationPageChange}
                handlePaginationClick={handlePaginationClick}
              >
                {reportContent}
              </ShowUserViewMobileLayout>
            ) : (
              <>
                <TabsMui
                  tabs={getUserTabConfig()}
                  activeTab={activeTab}
                  onChange={handleActiveTabChange}
                  scrollable
                  appearance="pill"
                  ariaLabel="Secciones del reporte"
                />
                <SharedUserReportHeader
                  dateRangeValue={dateRangeDraft}
                  onDateRangeChange={handleDateRangeChange}
                  onResetDateRange={handleDateRangeReset}
                  showReset={!isDefaultDateRange}
                  downloadProps={sharedHeaderDownloadProps}
                  compactPeriodNote
                />
                <div className="mt-30" />
                {reportContent}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ShowUserView
